/**
 * Minimal concurrency limiter.
 *
 * Permits are a QUEUE, not a cap on total work: passing 8 tasks to a
 * semaphore(3) runs all 8, never more than 3 at once. Tasks beyond the limit
 * await a promise that resolves when a permit frees up.
 *
 * Used to bound the two stages of a media batch independently:
 *
 *   const convertSem = semaphore(1);   // CPU-bound — parallel encodes just
 *                                      // split the same cores, at N× memory
 *   const uploadSem  = semaphore(3);   // network-bound — overlapping helps
 *
 *   const ops = files.map(async (f) => {
 *     const blob = await convertSem(() => convert(f));
 *     return uploadSem(() => upload(blob));
 *   });
 *   await Promise.allSettled(ops);
 *
 * Pipelining falls out for free — file 2 converts while file 1 uploads, with no
 * phase barrier — and memory stays bounded to a handful of live blobs rather
 * than every file at once.
 *
 * Hand-rolled rather than pulling in p-limit: it is thirteen lines, and p-limit
 * v4+ is ESM-only, which fights a Create React App / Jest setup.
 */
export function semaphore(max: number) {
  let active = 0;
  const queue: Array<() => void> = [];

  return async function run<T>(fn: () => Promise<T>): Promise<T> {
    if (active >= max) {
      await new Promise<void>((resolve) => queue.push(resolve));
    }
    active++;
    try {
      return await fn();
    } finally {
      active--;
      // Wake the next waiter, if any.
      queue.shift()?.();
    }
  };
}
